import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiMapPin, FiStar } from "react-icons/fi";
import { Clock, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const ExpertCard = ({
  id,
  name,
  isOnline,
  rating,
  reviewCount,
  specialization,
  experience,
  officeLocation,
  profileImage,
  documentsOverallStatus,
}) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  const statusText = isOnline ? "Online" : "Offline";

  const handleCardClick = (e) => {
    e.stopPropagation();
    if (id) {
      navigate(`/vendor-details/${id}`);
    } else {
      console.error("❌ Vendor ID is missing in card!");
    }
  };

  const initials = name
    ? name
        .split(" ")
        .filter(Boolean)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "EX";

  const hasNoImage =
    !profileImage ||
    profileImage.includes("flaticon.com") ||
    profileImage.includes("149071.png") ||
    imgError;

  return (
    <>
      <motion.div
        whileHover={{ y: -8 }}
        onClick={handleCardClick}
        className="w-full bg-white rounded-3xl overflow-hidden cursor-pointer border border-slate-100 hover:border-blue-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col h-[400px] group relative"
      >
        {/* Header Background */}
        <div className="h-24 bg-slate-50 group-hover:bg-blue-50/50 transition-colors duration-300" />

        {/* Status Badge - Floating Top Right */}
        <div className="absolute top-4 right-4 z-10">
          <div
            className={`px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-sm backdrop-blur-sm ${
              isOnline
                ? "bg-emerald-50/95 border border-emerald-100 text-emerald-600"
                : "bg-slate-100/95 border border-slate-200 text-slate-500"
            }`}
          >
            <span className="relative flex h-1.5 w-1.5">
              {isOnline && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
              )}
              <span
                className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                  isOnline ? "bg-emerald-600" : "bg-slate-400"
                }`}
              ></span>
            </span>
            <span className="text-[9px] font-bold tracking-wider uppercase">
              {statusText}
            </span>
          </div>
        </div>

        {/* Profile Avatar Section */}
        <div className="px-6 -mt-12 flex flex-col items-center flex-grow">
          <div className="relative">
            <div className="w-24 h-24 rounded-3xl overflow-hidden border-4 border-white shadow-md bg-slate-100 flex items-center justify-center shrink-0">
              {hasNoImage ? (
                <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-2xl tracking-wider">
                  {initials}
                </div>
              ) : (
                <img
                  src={
                    profileImage.startsWith("http")
                      ? profileImage
                      : `http://localhost:5014/${profileImage}`
                  }
                  alt={name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={() => setImgError(true)}
                />
              )}
            </div>
          </div>

          <div className="mt-4 w-full text-center flex flex-col flex-grow">
            <h3 className="text-base font-bold text-slate-800 group-hover:text-blue-600 transition-colors flex items-center justify-center gap-1 truncate px-2">
              {name}
              {documentsOverallStatus === "Verified" && (
                <ShieldCheck className="w-4 h-4 text-blue-500 shrink-0" fill="currentColor" />
              )}
            </h3>

            <div className="flex items-center justify-center gap-2 mt-1 mb-3">
              <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-md uppercase tracking-wider">
                {specialization || "Professional"}
              </span>
            </div>

            {/* Experience & Rating Bar */}
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-2xl mb-3 mt-auto">
              <div className="flex flex-col items-center flex-1">
                <span className="text-[8px] uppercase text-slate-400 font-bold tracking-wider">Experience</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3 text-slate-400" />
                  <span className="text-xs font-bold text-slate-700">{experience || 0} Yrs</span>
                </div>
              </div>
              <div className="w-px h-6 bg-slate-200"></div>
              <div className="flex flex-col items-center flex-1">
                <span className="text-[8px] uppercase text-slate-400 font-bold tracking-wider">Rating</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <FiStar className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-xs font-bold text-slate-700">
                    {rating && rating > 0 ? Number(rating).toFixed(1) : "4.9"}
                  </span>
                </div>
              </div>
            </div>

            {/* City Data */}
            <div className="flex items-center justify-center gap-1 text-slate-500 mb-4 mt-auto">
              <FiMapPin size={12} className="text-slate-400 shrink-0" />
              <span className="text-xs font-medium truncate max-w-[90%]">
                {officeLocation || "Available Online"}
              </span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="p-4 pt-0 mt-auto">
          <button
            onClick={handleCardClick}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-slate-900 group-hover:bg-blue-600 text-white text-xs font-bold rounded-xl transition-all duration-300"
          >
            <FiEye className="text-sm" />
            <span>View Profile</span>
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default ExpertCard;